import { isPublicId } from '@idc-forms/id'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import useAuth from '@/features/auth/hooks/use-auth'
import { useClientDetailQuery } from '@/features/clients/hooks/use-client-detail-query'
import { queryKeys } from '@/shared/constants/query-keys'
import { handleMutationError } from '@/shared/utils/handle-api-error'
import type { CreateAndSendPayload, SaveFieldReviewInput } from '../models/portal-form'
import { portalAdminV2Service } from '../services/portal-admin-v2.service'

/**
 * ☠️ **El id del cliente llega en DOS formas, y v2 sólo entiende una.**
 *
 * El filtro del listado v2 resuelve contra `clientPublicId` (`v2-portal-query-schemas.ts:29`), así
 * que un ObjectId **no da 400: no matchea nada**. El hub mostraría «este cliente no tiene flujos»
 * — silencioso, falso, e indistinguible de un cliente que de verdad no tiene ninguno.
 *
 * Y no es un caso de clientes viejos sin `cli_`: medido, no queda ninguno. Es de **qué loader
 * corrió**. `loadedClientRouteId` devuelve `original._id ?? original.id`, o sea que cuando el FEIC
 * se monta por la carga legacy la URL lleva el ObjectId aunque el cliente tenga su `cli_`.
 *
 * Por eso se normaliza acá: `useClientDetailQuery` ya corre en el router del portal, así que
 * react-query la deduplica y en el camino del hub no hay petición de más.
 *
 * **CUÁNDO SE BORRA ESTO:** junto con el puente de id (`clients/utils/client-route-id.ts`). Ese día
 * la URL siempre lleva el `cli_`, `isPublicId` es siempre cierto y esta función pasa a ser la
 * identidad — el typecheck no lo va a avisar, así que queda escrito acá.
 */
function useClientPublicId(clientId: string) {
  const yaEsPublico = isPublicId(clientId, 'cli')
  // La consulta sólo se dispara cuando hace falta traducir.
  const { data: client } = useClientDetailQuery(clientId, !yaEsPublico && Boolean(clientId))

  if (yaEsPublico) return clientId
  return client?.publicId ?? ''
}

/**
 * Los expedientes del portal de un cliente, contra el contrato v2 (carril D).
 *
 * ── QUÉ CAMBIA PARA QUIEN CONSUME ESTE HOOK ─────────────────────────────────
 *
 * 1. `flow.id` es un **`pfr_`**, no el uuid de v1. Lo que se guarde en una URL o en un estado
 *    persistido cambia de forma.
 * 2. **`flow.accessToken` ya no existe.** v2 no devuelve la credencial en ninguna respuesta de
 *    listado: el enlace se pide con `usePortalLink`, que lo copia sin dárselo al componente.
 * 3. El nombre del cliente vive en `flow.client.name`, no en `flow.clientName`.
 */
export function useClientPortalFlows(clientId: string) {
  const queryClient = useQueryClient()
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  const client = useClientPublicId(clientId)

  const flows = useQuery({
    queryKey: queryKeys.portalForms.detail(clientId, 'flows'),
    queryFn: async () => (await portalAdminV2Service.list(accessToken, { client })).data,
    // Sin `client` resuelto la consulta NO se dispara: pedirle a v2 el listado sin filtro
    // devolvería los expedientes de TODA la cartera, y la pantalla los mostraría como si fueran
    // de este cliente. Un filtro que se cae tiene que apagar la consulta, no ensancharla.
    enabled: Boolean(accessToken) && Boolean(client)
  })

  const invalidate = () => {
    // Prefijo del cliente: invalida TODAS las vistas de flujos del cliente — 'flows' (panel)
    // y 'active-flow' (router de revisión) — para que ambas reflejen el cambio.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.portalForms.detail(clientId)
    })
  }

  /**
   * ☠️ **El alta ya no le manda al servidor el tipo ni el nombre del cliente.**
   *
   * v2 los deriva del registro, y esa diferencia cierra un agujero de v1: la pantalla podía abrirle
   * un expediente `individual` a una persona jurídica, y el tipo decide **qué secciones tiene el
   * formulario**. El cliente lo llenaba entero antes de que nadie notara que era el KYC equivocado.
   *
   * Y crear **ya envía el correo**: v1 necesitaba dos llamadas y v2 no reproduce esa costura.
   */
  const createAndSend = useMutation({
    mutationFn: async (payload: CreateAndSendPayload) =>
      await portalAdminV2Service.create(accessToken, {
        client,
        flowDefinition: payload.flowDefinitionId,
        clientEmail: payload.clientEmail,
        expirationDays: payload.expirationDays
      }),
    onSuccess: () => {
      toast.success('Formulario enviado al cliente')
      invalidate()
    },
    onError: handleMutationError
  })

  const approve = useMutation({
    mutationFn: async (id: string) => await portalAdminV2Service.approve(accessToken, id),
    onSuccess: () => {
      toast.success('Expediente aprobado y sincronizado')
      invalidate()
    },
    onError: handleMutationError
  })

  const requestChanges = useMutation({
    mutationFn: async (vars: { id: string; comment: string }) =>
      await portalAdminV2Service.requestChanges(accessToken, vars.id, vars.comment),
    onSuccess: () => {
      toast.success('Correcciones solicitadas al cliente')
      invalidate()
    },
    onError: handleMutationError
  })

  const extendLink = useMutation({
    mutationFn: async (vars: { id: string; expirationDays?: number }) =>
      await portalAdminV2Service.extend(accessToken, vars.id, vars.expirationDays),
    onSuccess: () => {
      toast.success('Enlace renovado y reenviado al cliente')
      invalidate()
    },
    onError: handleMutationError
  })

  const resendLink = useMutation({
    mutationFn: async (id: string) => await portalAdminV2Service.resend(accessToken, id),
    onSuccess: () => {
      toast.success('Correo reenviado al cliente')
      invalidate()
    },
    onError: handleMutationError
  })

  return {
    flows: flows.data,
    isLoading: flows.isLoading,
    createAndSend,
    approve,
    requestChanges,
    extendLink,
    resendLink
  }
}

/**
 * Review of a single flow (admin): detail (submitted applicant + field reviews +
 * derived section summary) and incremental save-as-you-go of field marks.
 */
export function usePortalFlowReview(flowId: string) {
  const queryClient = useQueryClient()
  const { auth } = useAuth()
  const accessToken = auth?.v2AccessToken ?? ''
  const detailKey = queryKeys.portalForms.detail(flowId, 'review')

  const detail = useQuery({
    queryKey: detailKey,
    queryFn: async () => await portalAdminV2Service.detail(accessToken, flowId),
    enabled: Boolean(flowId) && Boolean(accessToken)
  })

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: detailKey })
    // The clients table shows the flow status/validation columns.
    void queryClient.invalidateQueries({ queryKey: queryKeys.clients.all })
  }

  const reviewFields = useMutation({
    mutationFn: async (reviews: SaveFieldReviewInput[]) =>
      await portalAdminV2Service.saveFieldReviews(accessToken, flowId, reviews),
    // Refetch the GET detail (it derives sectionReview, which the mutation
    // response omits) so the section summary / progress stay consistent.
    onSuccess: invalidate,
    onError: handleMutationError
  })

  const removeFieldReview = useMutation({
    mutationFn: async (target: { field: string; section: string }) =>
      await portalAdminV2Service.deleteFieldReview(
        accessToken,
        flowId,
        target.field,
        target.section
      ),
    onSuccess: () => {
      toast.success('Corrección eliminada')
      invalidate()
    },
    onError: handleMutationError
  })

  const takeOver = useMutation({
    mutationFn: async () => await portalAdminV2Service.takeOver(accessToken, flowId),
    onSuccess: () => {
      toast.success('Expediente en revisión')
      invalidate()
    },
    onError: handleMutationError
  })

  const approve = useMutation({
    mutationFn: async () => await portalAdminV2Service.approve(accessToken, flowId),
    onSuccess: () => {
      toast.success('Expediente aprobado y sincronizado')
      invalidate()
    },
    onError: handleMutationError
  })

  const requestChanges = useMutation({
    mutationFn: async (comment?: string) =>
      await portalAdminV2Service.requestChanges(accessToken, flowId, comment),
    onSuccess: () => {
      toast.success('Correcciones solicitadas al cliente')
      invalidate()
    },
    onError: handleMutationError
  })

  const reject = useMutation({
    mutationFn: async (reason: string) =>
      await portalAdminV2Service.reject(accessToken, flowId, reason),
    onSuccess: () => {
      toast.success('Expediente rechazado y notificado al cliente')
      invalidate()
    },
    onError: handleMutationError
  })

  const revoke = useMutation({
    mutationFn: async (reason?: string) =>
      await portalAdminV2Service.revoke(accessToken, flowId, reason),
    onSuccess: () => {
      toast.success('Flujo cancelado')
      invalidate()
    },
    onError: handleMutationError
  })

  // Adjuntos del expediente gestionados por el asesor (subir/borrar en nombre del cliente).
  // Refetch del detalle: trae `documents` con su URL firmada y el `sectionReview` derivado.
  const uploadDocument = useMutation({
    mutationFn: async (vars: { requirementId: string; file: File }) =>
      await portalAdminV2Service.uploadDocument(accessToken, flowId, vars.requirementId, vars.file),
    onSuccess: invalidate,
    onError: handleMutationError
  })

  const deleteDocument = useMutation({
    mutationFn: async (documentId: string) =>
      await portalAdminV2Service.deleteDocument(accessToken, flowId, documentId),
    onSuccess: () => {
      toast.success('Documento eliminado')
      invalidate()
    },
    onError: handleMutationError
  })

  return {
    detail: detail.data,
    isLoading: detail.isLoading,
    isError: detail.isError,
    // El MOTIVO, no sólo el hecho. Sin esto la pantalla sólo puede decir «no cargó», que es
    // exactamente lo que no sirve cuando el fallo es del contrato y el servidor devolvió 200.
    error: detail.error as unknown,
    reviewFields,
    removeFieldReview,
    takeOver,
    approve,
    requestChanges,
    reject,
    revoke,
    /** Sube un documento a un slot; awaitable para encadenar el preview del uploader. */
    uploadDocument: async (
      requirementId: string,
      file: File
    ): Promise<{ id: string; name: string }> =>
      await uploadDocument.mutateAsync({ requirementId, file }),
    removeDocument: (documentId: string) => {
      deleteDocument.mutate(documentId)
    }
  }
}
